import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import {
  FaUserTie,
  FaBuilding,
  FaBook,
  FaMoneyBillWave,
  FaArrowLeft,
  FaCheck,
  FaCalendarAlt,
  FaUniversity,
  FaGraduationCap,
  FaFileAlt,
  FaClock,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import useAuth from "../../useAuth";
import { server } from "../../server";
import compressImage from "../../utils/compressAsset";

const styles = {
  sectionTitle:
    "text-sm font-black text-slate-800 dark:text-slate-100 border-b border-indigo-100 dark:border-slate-800 pb-1.5 mb-3 mt-4 flex items-center gap-2 uppercase tracking-wide",
  label: "block text-[11px] font-bold text-slate-600 dark:text-slate-300 mb-1 tracking-tight",
  input:
    "w-full px-3 py-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all duration-150 bg-slate-50/50 dark:bg-slate-800/80 focus:bg-white dark:focus:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 hover:border-indigo-300 dark:hover:border-slate-600 font-semibold",
  select:
    "w-full px-3 py-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-slate-50/50 dark:bg-slate-800/80 focus:bg-white dark:focus:bg-slate-900 text-slate-900 dark:text-white hover:border-indigo-300 dark:hover:border-slate-600 font-semibold",
  fileInput:
    "block w-full text-xs text-slate-500 dark:text-slate-400 file:mr-2.5 file:py-1 file:px-2.5 file:rounded-md file:border-0 file:text-[11px] file:font-bold file:bg-indigo-50 dark:file:bg-indigo-950/60 file:text-indigo-700 dark:file:text-indigo-300 hover:file:bg-indigo-100 transition-all cursor-pointer border border-slate-200 dark:border-slate-700 rounded-lg p-0.5 bg-white dark:bg-slate-800",
  error: "text-[10px] text-red-500 dark:text-red-400 flex items-center mt-0.5 font-bold",
  card: "bg-white dark:bg-slate-900 p-4 md:p-5 rounded-2xl shadow-xs border border-slate-100 dark:border-slate-800",
};

const InputGroup = ({ label, children, error, required }) => (
  <div className="flex flex-col">
    <label className={styles.label}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative group">{children}</div>
    {error && <p className={styles.error}>{error.message}</p>}
  </div>
);

const FilePreview = ({ label, url, isImage }) => {
  if (!url || typeof url !== "string" || url === "") return null;
  return (
    <div className="mt-1.5 p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
          Current {label}
        </span>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-indigo-600 dark:text-indigo-400 font-extrabold hover:underline"
        >
          Open
        </a>
      </div>
      {isImage ? (
        <img
          src={url}
          alt={label}
          className="w-full h-20 object-contain rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
        />
      ) : (
        <div className="p-1.5 flex items-center justify-center bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] font-bold text-slate-600 dark:text-slate-300 hover:text-indigo-600 truncate max-w-full"
          >
            View Document
          </a>
        </div>
      )}
    </div>
  );
};

export default function EmployeeRegistration() {
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      Qualifications: [{ Degree: "", Institute: "", Year: "", Percentage: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "Qualifications",
  });

  const [departments, setDepartments] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const { token } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const aadhaarFileUrl = watch("aadhaarFile");
  const panFileUrl = watch("panFile");
  const watchedPhotoFile = watch("PhotoFile");

  // Profile photo preview state
  const [photoPreview, setPhotoPreview] = useState(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      try {
        const [deptRes, subRes] = await Promise.all([
          axios.get(`${server}/departments`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${server}/subjects`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setDepartments(deptRes.data.data || []);
        setSubjects(subRes.data.data || []);

        // If Edit mode, fetch employee details
        if (isEdit) {
          const empRes = await axios.get(`${server}/employees/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const empData = empRes.data.data;

          const mappedData = {};
          Object.keys(empData).forEach((key) => {
            mappedData[key] = empData[key];
            const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
            mappedData[pascalKey] = empData[key];
          });

          // Format dates for input fields
          if (mappedData.JoiningDate)
            mappedData.JoiningDate = mappedData.JoiningDate.split("T")[0];
          if (mappedData.SalaryDate)
            mappedData.SalaryDate = mappedData.SalaryDate.split("T")[0];

          // Ensure Qualifications array is present for useFieldArray
          if (
            !mappedData.Qualifications ||
            mappedData.Qualifications.length === 0
          ) {
            mappedData.Qualifications = [
              { Degree: "", Institute: "", Year: "", Percentage: "" },
            ];
          }

          reset(mappedData);
          // Store existing photo URL for preview
          if (empData.profilePictureUrl || empData.ProfilePictureUrl) {
            setExistingPhotoUrl(
              empData.profilePictureUrl || empData.ProfilePictureUrl,
            );
          }
        }
      } catch (e) {
        console.error(e);
        toast.error("Failed to load data");
      }
    };
    fetchData();
  }, [token, id, isEdit, reset]);

  const onSubmit = async (data) => {
    try {
      const url = isEdit ? `${server}/employees/${id}` : `${server}/employees`;

      const method = isEdit ? "put" : "post";

      const formData = new FormData();

      const payload = {
        ...data,
        DepartmentId: Number(data.DepartmentId),
        SubjectId: data.SubjectId ? Number(data.SubjectId) : null,
        Salary: Number(data.Salary),
        ExperienceYears: Number(data.ExperienceYears),
        IsActive: data.IsActive === true || data.IsActive === "true",
      };

      // Handle regular fields
      Object.keys(payload).forEach((key) => {
        if (
          key !== "AadhaarFileDetails" &&
          key !== "PANFileDetails" &&
          key !== "ResumeFileDetails" &&
          key !== "PassbookFileDetails" &&
          key !== "Qualifications" &&
          key !== "PhotoFile"
        ) {
          formData.append(key, payload[key] === null ? "" : payload[key]);
        }
      });

      // Handle Qualifications - separate fields and files
      data.Qualifications.forEach((qual, index) => {
        formData.append(`Qualifications[${index}].Degree`, qual.Degree);
        formData.append(`Qualifications[${index}].Institute`, qual.Institute);
        formData.append(`Qualifications[${index}].Year`, qual.Year);
        formData.append(`Qualifications[${index}].Percentage`, qual.Percentage);

        if (qual.File && qual.File[0]) {
          formData.append(`QualificationFiles_${index}`, qual.File[0]);
        }
      });

      // Handle Files
      if (data.PhotoFile && data.PhotoFile[0]) {
        const originalFile = data.PhotoFile[0];
        try {
          const compressed = await compressImage(originalFile);
          formData.append(
            "Photo",
            compressed ?? originalFile,
            originalFile.name,
          );
        } catch {
          formData.append("Photo", originalFile, originalFile.name);
        }
      }
      if (data.AadhaarFileDetails && data.AadhaarFileDetails[0])
        formData.append("AadhaarFileDetails", data.AadhaarFileDetails[0]);
      if (data.PANFileDetails && data.PANFileDetails[0])
        formData.append("PANFileDetails", data.PANFileDetails[0]);
      if (data.ResumeFile && data.ResumeFile[0])
        formData.append("ResumeFileDetails", data.ResumeFile[0]);
      if (data.PassbookFile && data.PassbookFile[0])
        formData.append("PassbookFileDetails", data.PassbookFile[0]);

      await axios[method](url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success(isEdit ? "Employee updated!" : "Employee registered!");
      navigate("/my/employees");
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving employee.");
      console.error(error);
    }
  };

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      <div className="space-y-4">
        {/* Compact Top Header Bar */}
        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
              <FaUserTie size={18} />
            </div>
            <div>
              <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">
                {isEdit ? "Update Employee Profile" : "New Employee Registration"}
              </h1>
              <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">
                {isEdit ? "Edit existing staff employment & compensation records" : "Enter details to onboard a new school staff member"}
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate("/my/employees")}
            className="bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-300 px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <FaArrowLeft size={10} /> Back to Directory
          </button>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4 fade-in-up"
        >
          <div className={styles.card}>
            {/* Personal Details */}
            <h3 className={styles.sectionTitle}>
              <FaUserTie className="text-indigo-500 dark:text-indigo-400" /> Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
              <InputGroup label="Full Name" required error={errors.Name}>
                <input
                  {...register("Name", { required: "Name is required" })}
                  className={styles.input}
                  placeholder="e.g. Employee Name"
                />
              </InputGroup>

              <InputGroup
                label="Father's Name"
                required
                error={errors.FatherName}
              >
                <input
                  {...register("FatherName", {
                    required: "Father's Name is required",
                  })}
                  className={styles.input}
                  placeholder="Father's Name"
                />
              </InputGroup>

              <InputGroup
                label="Mother's Name"
                required
                error={errors.MotherName}
              >
                <input
                  {...register("MotherName", {
                    required: "Mother's Name is required",
                  })}
                  className={styles.input}
                  placeholder="Mother's Name"
                />
              </InputGroup>

              <InputGroup label="Gender" required error={errors.Gender}>
                <select
                  {...register("Gender", { required: "Gender is required" })}
                  className={styles.select}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </InputGroup>

              <InputGroup label="Mobile No" required error={errors.MobileNo}>
                <input
                  {...register("MobileNo", {
                    required: "Mobile No is required",
                  })}
                  className={styles.input}
                  placeholder="10-digit Mobile No"
                  maxLength={10}
                />
              </InputGroup>

              <InputGroup label="Email" required error={errors.Email}>
                <input
                  type="email"
                  {...register("Email", { required: "Email is required" })}
                  className={styles.input}
                  placeholder="email@example.com"
                />
              </InputGroup>

              <div className="md:col-span-3">
                <InputGroup label="Address" required error={errors.Address}>
                  <textarea
                    {...register("Address", {
                      required: "Address is required",
                    })}
                    className={`${styles.input} min-h-[45px] resize-y py-1.5`}
                    placeholder="Full Residential Address"
                    rows={1.5}
                  />
                </InputGroup>
              </div>

              <div className="lg:col-span-3">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  {/* Preview area */}
                  <div className="relative flex-shrink-0">
                    <div className="w-20 h-20 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 shadow-2xs">
                      {photoPreview ? (
                        <img
                          src={photoPreview}
                          alt="New photo"
                          className="w-full h-full object-cover"
                        />
                      ) : existingPhotoUrl ? (
                        <img
                          src={existingPhotoUrl}
                          alt="Current photo"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 dark:text-slate-500">
                          <FaUserTie className="text-2xl mb-1" />
                          <span className="text-[10px]">No photo</span>
                        </div>
                      )}
                    </div>

                    {photoPreview && existingPhotoUrl && (
                      <div
                        className="absolute -top-2 -right-2 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center shadow-md"
                        title="Photo will be replaced"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="white"
                          className="w-3.5 h-3.5"
                        >
                          <path d="M21 6.5A4.5 4.5 0 0016.5 2H12v2h4.5a2.5 2.5 0 010 5H5.41l2.3-2.29-1.42-1.42L2 9.5l4.3 4.21 1.41-1.42L5.41 11H16.5A4.5 4.5 0 0021 6.5z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* File input + instructions */}
                  <div className="flex-1">
                    <p className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400 tracking-wider mb-2">
                      {isEdit
                        ? "Replace Photograph"
                        : "Upload Photograph (Max 1 MB)"}
                      {!isEdit && <span className="text-red-500 ml-1">*</span>}
                    </p>

                    <label className="cursor-pointer flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-indigo-400 dark:hover:border-indigo-500 bg-slate-50 dark:bg-slate-800/80 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 transition-all group">
                      <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 group-hover:bg-indigo-200 dark:group-hover:bg-indigo-900 flex items-center justify-center text-indigo-600 dark:text-indigo-400 transition-colors flex-shrink-0">
                        <svg
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="w-4 h-4"
                        >
                          <path
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                          {photoPreview ? (
                            <span className="text-indigo-600 dark:text-indigo-400">
                              ✓ New photo selected — will replace existing
                            </span>
                          ) : isEdit ? (
                            "Click to select a new photo"
                          ) : (
                            "Click to select photo"
                          )}
                        </p>
                        <p className="text-xs text-slate-400 dark:text-slate-400">
                          JPG, PNG, WEBP · max 1MB
                        </p>
                      </div>
                      <input
                        type="file"
                        {...register("PhotoFile", {
                          validate: {
                            required: (value) =>
                              !isEdit && (!value || value.length === 0)
                                ? "Photo is required"
                                : true,
                            size: (files) => {
                              if (!files || files.length === 0) return true;
                              return (
                                files[0].size <= 1048576 ||
                                "Photo must be less than 1 MB"
                              );
                            },
                          },
                        })}
                        onChange={(e) => {
                          register("PhotoFile").onChange(e);
                          const file = e.target.files?.[0];
                          if (file) {
                            setPhotoPreview(URL.createObjectURL(file));
                          } else {
                            setPhotoPreview(null);
                          }
                        }}
                        accept="image/*"
                        className="hidden"
                      />
                    </label>

                    {errors.PhotoFile && (
                      <p className="text-xs text-red-500 dark:text-red-400 mt-1 flex items-center gap-1">
                        ⚠ {errors.PhotoFile.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Details */}
            <h3 className={styles.sectionTitle}>
              <FaBuilding className="text-indigo-500 dark:text-indigo-400" /> Professional
              Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
              {isEdit && (
                <InputGroup label="Employee Code (Read Only)">
                  <input
                    disabled
                    {...register("EmployeeCode")}
                    className={`${styles.input} bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 cursor-not-allowed`}
                  />
                </InputGroup>
              )}

              <InputGroup
                label="Designation"
                required
                error={errors.Designation}
              >
                <input
                  {...register("Designation", {
                    required: "Designation is required",
                  })}
                  className={styles.input}
                  placeholder="e.g. Senior Teacher"
                />
              </InputGroup>

              <InputGroup
                label="Department"
                required
                error={errors.DepartmentId}
              >
                <div className="relative">
                  <FaBuilding className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs z-10" />
                  <select
                    {...register("DepartmentId", {
                      required: "Department is required",
                    })}
                    className={`${styles.select} pl-8`}
                  >
                    <option value="">Select Department</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              </InputGroup>

              <InputGroup
                label="Core Subject"
                required
                error={errors.SubjectId}
              >
                <div className="relative">
                  <FaBook className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs z-10" />
                  <select
                    {...register("SubjectId", {
                      required: "Subject is required",
                    })}
                    className={`${styles.select} pl-8`}
                  >
                    <option value="">Select Subject</option>
                    {subjects.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.code})
                      </option>
                    ))}
                  </select>
                </div>
              </InputGroup>

              <InputGroup label="Section" required error={errors.Section}>
                <select
                  {...register("Section", { required: "Section is required" })}
                  className={styles.select}
                >
                  <option value="">Select Section</option>
                  <option value="Junior Section">Junior Section</option>
                  <option value="Senior Section">Senior Section</option>
                  <option value="Higher Secondary Section">
                    Higher Secondary Section
                  </option>
                </select>
              </InputGroup>

              <InputGroup
                label="Working Hours"
                required
                error={errors.WorkingHours}
              >
                <div className="relative">
                  <FaClock className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs z-10" />
                  <input
                    {...register("WorkingHours", {
                      required: "Working Hours is required",
                    })}
                    className={`${styles.input} pl-8`}
                    placeholder="e.g. 9:00 AM - 5:00 PM"
                  />
                </div>
              </InputGroup>

              <div className="lg:col-span-3">
                <InputGroup
                  label="Upload Resume/CV (Max 1 MB)"
                  required
                  error={errors.ResumeFile}
                >
                  <div className="relative">
                    <FaFileAlt className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs z-10" />
                    <input
                      type="file"
                      {...register("ResumeFile", {
                        validate: {
                          required: (value) =>
                            !isEdit && (!value || value.length === 0)
                              ? "Resume is required"
                              : true,
                          size: (files) => {
                            if (!files || files.length === 0) return true;
                            return (
                              files[0].size <= 1048576 ||
                              "File must be less than 1 MB"
                            );
                          },
                        },
                      })}
                      accept=".pdf,.doc,.docx"
                      className={`${styles.fileInput} pl-8`}
                    />
                  </div>
                </InputGroup>
              </div>
            </div>

            {/* Employment Terms */}
            <h3 className={styles.sectionTitle}>
              <FaMoneyBillWave className="text-indigo-500 dark:text-indigo-400" /> Employment Terms
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3.5">
              <InputGroup
                label="Joining Date"
                required
                error={errors.JoiningDate}
              >
                <input
                  type="date"
                  {...register("JoiningDate", {
                    required: "Joining Date is required",
                  })}
                  className={styles.input}
                  max={new Date()?.toISOString().split("T")[0]}
                />
              </InputGroup>

              <InputGroup
                label="Salary Date"
                required
                error={errors.SalaryDate}
              >
                <div className="relative">
                  <FaCalendarAlt className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs z-10" />
                  <input
                    type="date"
                    {...register("SalaryDate", {
                      required: "Salary Date is required",
                    })}
                    className={`${styles.input} pl-8`}
                  />
                </div>
              </InputGroup>

              <InputGroup label="Monthly Salary" required error={errors.Salary}>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 text-xs font-bold">
                    ₹
                  </span>
                  <input
                    type="number"
                    {...register("Salary", { required: "Salary is required" })}
                    className={`${styles.input} pl-6`}
                    placeholder="0.00"
                  />
                </div>
              </InputGroup>

              <InputGroup
                label="Experience (Years)"
                required
                error={errors.ExperienceYears}
              >
                <input
                  type="number"
                  {...register("ExperienceYears", {
                    required: "Experience is required",
                  })}
                  className={styles.input}
                  defaultValue={0}
                />
              </InputGroup>
            </div>

            {/* Bank Details */}
            <h3 className={styles.sectionTitle}>
              <FaUniversity className="text-indigo-500 dark:text-indigo-400" /> Bank Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
              <InputGroup label="Bank Name" required error={errors.BankName}>
                <input
                  {...register("BankName", {
                    required: "Bank Name is required",
                  })}
                  className={styles.input}
                  placeholder="e.g. SBI"
                />
              </InputGroup>
              <InputGroup
                label="Account Number"
                required
                error={errors.BankAccountNo}
              >
                <input
                  {...register("BankAccountNo", {
                    required: "Account Number is required",
                    pattern: {
                      value: /^\d{9,18}$/,
                      message: "Account number must be 9-18 digits",
                    },
                  })}
                  className={styles.input}
                  placeholder="Account No"
                />
              </InputGroup>
              <InputGroup label="IFSC Code" required error={errors.IFSCCode}>
                <input
                  {...register("IFSCCode", {
                    required: "IFSC Code is required",
                    pattern: {
                      value: /^[A-Z]{4}0[A-Z0-9]{6}$/i,
                      message: "Invalid IFSC format (e.g. SBIN0123456)",
                    },
                  })}
                  className={styles.input}
                  placeholder="IFSC Code"
                />
              </InputGroup>
              <div className="lg:col-span-3">
                <InputGroup
                  label="Upload Passbook/Cheque Photo (Max 1 MB)"
                  required
                  error={errors.PassbookFile}
                >
                  <input
                    type="file"
                    {...register("PassbookFile", {
                      validate: {
                        required: (value) =>
                          !isEdit && (!value || value.length === 0)
                            ? "Passbook is required"
                            : true,
                        size: (files) => {
                          if (!files || files.length === 0) return true;
                          return (
                            files[0].size <= 1048576 ||
                            "File must be less than 1 MB"
                          );
                        },
                      },
                    })}
                    accept="image/*"
                    className={styles.fileInput}
                  />
                </InputGroup>
              </div>
            </div>

            {/* Educational Qualifications */}
            <h3 className={styles.sectionTitle}>
              <FaGraduationCap className="text-indigo-500 dark:text-indigo-400" /> Educational
              Qualifications
            </h3>
            <div className="space-y-2.5">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2.5 p-3 bg-slate-50/70 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800 relative"
                >
                  <InputGroup
                    label="Degree/Title"
                    required
                    error={errors?.Qualifications?.[index]?.Degree}
                  >
                    <input
                      {...register(`Qualifications.${index}.Degree`, {
                        required: "Degree is required",
                      })}
                      className={styles.input}
                      placeholder="e.g. B.Tech"
                    />
                  </InputGroup>
                  <InputGroup
                    label="Institute/University"
                    required
                    error={errors?.Qualifications?.[index]?.Institute}
                  >
                    <input
                      {...register(`Qualifications.${index}.Institute`, {
                        required: "Institute is required",
                      })}
                      className={styles.input}
                      placeholder="Institute Name"
                    />
                  </InputGroup>
                  <InputGroup
                    label="Year"
                    required
                    error={errors?.Qualifications?.[index]?.Year}
                  >
                    <input
                      {...register(`Qualifications.${index}.Year`, {
                        required: "Year is required",
                        pattern: {
                          value: /^(19|20)\d{2}$/,
                          message: "Valid 4-digit year required (e.g., 2023)",
                        },
                      })}
                      className={styles.input}
                      placeholder="Passing Year"
                    />
                  </InputGroup>
                  <InputGroup
                    label="Percentage/CGPA"
                    required
                    error={errors?.Qualifications?.[index]?.Percentage}
                  >
                    <input
                      {...register(`Qualifications.${index}.Percentage`, {
                        required: "Percentage is required",
                        pattern: {
                          value: /^(100(\.0{1,2})?|[3-9]\d(\.\d{1,2})?)$/,
                          message: "Must be a valid number between 30-100",
                        },
                      })}
                      className={styles.input}
                      placeholder="%"
                    />
                  </InputGroup>
                  <InputGroup
                    label="Certificate"
                    required
                    error={errors?.Qualifications?.[index]?.File}
                  >
                    <input
                      type="file"
                      {...register(`Qualifications.${index}.File`, {
                        validate: (value) =>
                          !isEdit && (!value || value.length === 0)
                            ? "Certificate is required"
                            : true,
                      })}
                      className={styles.fileInput}
                    />
                  </InputGroup>

                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="absolute top-2 right-2 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full p-1 shadow-sm"
                    >
                      <FaTrash size={12} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  append({
                    Degree: "",
                    Institute: "",
                    Year: "",
                    Percentage: "",
                  })
                }
                className="text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center gap-2"
              >
                <FaPlus /> Add More Qualification
              </button>
            </div>

            {/* Identity Documents */}
            <h3 className={styles.sectionTitle}>
              <FaCheck className="text-indigo-500 dark:text-indigo-400" /> Identity Documents
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              <div className="space-y-2.5">
                <InputGroup
                  label="Aadhaar Number"
                  required
                  error={errors.AadhaarNo}
                >
                  <input
                    {...register("AadhaarNo", {
                      required: "Aadhaar No is required",
                      pattern: {
                        value: /^\d{12}$/,
                        message: "Aadhaar must be exactly 12 digits",
                      },
                    })}
                    className={styles.input}
                    placeholder="XXXX XXXX XXXX"
                  />
                </InputGroup>
                <InputGroup
                  label="Upload Aadhaar Card (Max 1 MB)"
                  required
                  error={errors.AadhaarFileDetails}
                >
                  <input
                    type="file"
                    {...register("AadhaarFileDetails", {
                      validate: {
                        required: (value) =>
                          !isEdit && (!value || value.length === 0)
                            ? "Aadhaar Card is required"
                            : true,
                        size: (files) => {
                          if (!files || files.length === 0) return true;
                          return (
                            files[0].size <= 1048576 ||
                            "File must be less than 1 MB"
                          );
                        },
                      },
                    })}
                    accept="image/*,application/pdf"
                    className={styles.fileInput}
                  />
                  <FilePreview
                    label="Aadhaar"
                    url={aadhaarFileUrl}
                    isImage={false}
                  />
                </InputGroup>
              </div>

              <div className="space-y-2.5">
                <InputGroup label="PAN Number" required error={errors.PANNo}>
                  <input
                    {...register("PANNo", {
                      required: "PAN No is required",
                      pattern: {
                        value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i,
                        message: "Invalid PAN format (e.g. ABCDE1234F)",
                      },
                    })}
                    className={styles.input}
                    placeholder="XXXXX1234X"
                  />
                </InputGroup>
                <InputGroup
                  label="Upload PAN Card (Max 1 MB)"
                  required
                  error={errors.PANFileDetails}
                >
                  <input
                    type="file"
                    {...register("PANFileDetails", {
                      validate: {
                        required: (value) =>
                          !isEdit && (!value || value.length === 0)
                            ? "PAN Card is required"
                            : true,
                        size: (files) => {
                          if (!files || files.length === 0) return true;
                          return (
                            files[0].size <= 1048576 ||
                            "File must be less than 1 MB"
                          );
                        },
                      },
                    })}
                    accept="image/*,application/pdf"
                    className={styles.fileInput}
                  />
                  <FilePreview
                    label="PAN Card"
                    url={panFileUrl}
                    isImage={false}
                  />
                </InputGroup>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 bg-white dark:bg-slate-900 p-2.5 px-4 rounded-xl border border-slate-200 dark:border-slate-800 sticky bottom-3 z-20 shadow-sm">
            <button
              type="button"
              onClick={() => navigate("/my/employees")}
              className="px-5 py-1.5 text-xs font-extrabold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-1.5 text-xs font-black text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-500 rounded-lg shadow-xs disabled:opacity-70 disabled:cursor-not-allowed transition-all flex items-center gap-1.5"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin h-3.5 w-3.5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <FaCheck size={10} />
                  {isEdit ? "Update Staff Member" : "Register Staff Member"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
